//
// 03/05/2019 note:
// When changing any site settings, be sure to follow the process in the "Settings Support Work In-take Process" wiki page:
//    https://mytableau.tableaucorp.com/x/coDWD
//

import * as _ from 'underscore';

import { NumberInputRange } from '../../ts/directives/tbRangeInputValidator';
import { SiteIdValidRegExp } from '../../ts/directives/tbSiteIdInputValidator';
import { ByteSizeConversionFactor } from '../../ts/filters/SizeFilter';
import { L10n } from '../../ts/L10n/L10n';
import { Module } from '../../ts/Module';
import { SiteAvailabilityState, Sites } from '../../ts/resources/Sites';
import { GetNumExcessRevisionsRequest, IGetNumExcessRevisionsParams, IGetNumExcessRevisionsResult } from '../../ts/ServerApi/requests/GetNumExcessRevisionsRequest';
import { IError } from '../../ts/ServerApi/types/IError';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ClearSavedRevisionsAction } from '../../ts/services/ClearSavedRevisionsAction';
import { HelpService } from '../../ts/services/HelpService';
import { ILicensingInfoWithTotals, LicensesService } from '../../ts/services/LicensesService';
import { ServerService } from '../../ts/services/ServerService';
import { SiteUrlMatcherFactory } from '../../ts/services/SiteUrlMatcherFactory';
import { ISettingsCoordinatorCtrlScope, SettingsFormService } from '../directives/tbSettingsCoordinator';
import { ISiteSettingsForServerAdmin } from '../interfaces/ISite';

export interface ISiteSettingsForServerAdminFormScope extends ISettingsCoordinatorCtrlScope {

  siteSettingsForServerAdminForm: ng.IFormController; // from siteSettingsForServerAdminForm.pug

  hideAvailability: boolean;
  hideRevisionHistoryLimitWarning: boolean;
  autoSelectSiteName: boolean;
  active: string;
  suspended: string;
  url: string;
  defaultSubscriptionsEmail: string;
  userCountMax: number;
  storageQuotaMax: number;
  versionLimitMin: number;
  versionLimitMax: number;
  isSeatLicensing: boolean;
  isSubscriptionLicensing: boolean;
  server: ServerService;
  serverLevelSubscriptionsEnabled: boolean; // Resolved in createSiteModal controller
  unbindNameWatcher: Function;
  clearSavedRevisions: () => void;
  excessRevisionsMessage: string;
  subscriptionsEnabledOnServer: boolean;
  isGuestEnabledOnServer: () => boolean;
  isSessionMultiSite: () => boolean;
  cacheWarmupLearnMoreHref: string;
  showSiteLevelGuestAccessControl: () => boolean;
  isDefaultSite: boolean;
  isSelfServiceScheduleEnabled: boolean;
  isCustomScheduleSubscriptionForSiteEnabled: boolean;
  flowsOnServerEnabled: boolean;
  catalogingOnServerEnabled: boolean;
  alternativeFederationEngineUserInterface: boolean;
}

export class SiteSettingsForServerAdminFormCtrl {

  private master: ISiteSettingsForServerAdmin;

  public static $inject = [
    '$scope',
    '$location',
    '$q',
    '$translate',
    '$state',
    'SettingsFormService',
    'ClearSavedRevisionsAction',
    'ServerService',
    'Sites',
    'SiteUrlMatcherFactory',
    'LicensesService',
    'HelpService',
    '$timeout',
  ];

  public static InputDebounceMs: number = 200;

  constructor(
    private scope: ISiteSettingsForServerAdminFormScope,
    private $location: ng.ILocationService,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private $state: ng.ui.IStateService,
            formService: SettingsFormService,
    private clearSavedRevisionsAction: ClearSavedRevisionsAction,
    private server: ServerService,
    private sitesResource: Sites,
    private siteUrlMatcherFactory: SiteUrlMatcherFactory,
            licenses: LicensesService,
    private help: HelpService,
    $timeout: ng.ITimeoutService
  ) {
    if (this.scope.isDefaultSite == null) {
     this.scope.isDefaultSite = this.scope.siteSettingsForServerAdmin.settings
      && this.scope.siteSettingsForServerAdmin.settings.urlName === '';
    }

    this.scope.active = SiteAvailabilityState.ACTIVE;
    this.scope.suspended = SiteAvailabilityState.SUSPENDED;
    this.scope.defaultSubscriptionsEmail = this.server.defaultSubscriptionsEmail();
    licenses.fetch().then((licensingInfo: ILicensingInfoWithTotals) => {
      if (licensingInfo) {
        const noLicenses = !licensingInfo.productKeyInfos || !licensingInfo.productKeyInfos.length;
        if (licensingInfo.coreLicensingInfos || noLicenses) {
          this.scope.userCountMax = NumberInputRange.INT.MAX;
        } else { //Not core so compute the maximum users
          this.scope.userCountMax = licensingInfo.totalExplorerUsers;
          if(licensingInfo.totalCreatorUsers){
            this.scope.userCountMax+=licensingInfo.totalCreatorUsers;
          }
          if(licensingInfo.totalViewerUsers){
            this.scope.userCountMax+=licensingInfo.totalViewerUsers;
          }
        }
      }
    });
    this.scope.storageQuotaMax = NumberInputRange.LONG.MAX / ByteSizeConversionFactor.ONE_GB;
    this.scope.versionLimitMin = 2;
    this.scope.versionLimitMax = 10000;
    this.scope.server = this.server;

    this.scope.isGuestEnabledOnServer = () => this.server.isGuestEnabled();
    this.scope.isSessionMultiSite= () => this.server.isSessionMultiSite();
    this.scope.showSiteLevelGuestAccessControl= () => (!this.scope.isDefaultSite && !this.scope.isSessionMultiSite()) || this.scope.isSessionMultiSite();
    this.scope.cacheWarmupLearnMoreHref = this.help.getServerHelpUrlByCategory('workbookrefresh');

    this.initializeUnsetValues();

    this.master = angular.copy(this.scope.siteSettingsForServerAdmin);

    this.scope.sharedForm = formService;
    this.scope.localForm = {
        formController: this.scope.siteSettingsForServerAdminForm,
        save: () => this.getSiteSettingsForServerAdminUpdatePromise(),
        cancel: () => this.revertChanges(),
        isBusy: () => this.scope.localForm.busy,
        isFormUnchanged: () => angular.equals(this.master, this.scope.siteSettingsForServerAdmin),
        resetSettings: null
    };

    this.scope.$watch('siteSettingsForServerAdmin.settings.urlName', (value: string) => {
      scope.url = this.$location.absUrl().replace(this.$location.url(), '') +
        this.siteUrlMatcherFactory.compile('').format({siteUrlName: value});
    });

    this.scope.$watch('siteSettingsForServerAdmin.settings.siteCommentingSettings.commentingEnabled', (value: boolean) => {
      if (this.scope.siteSettingsForServerAdmin.settings.siteCommentingSettings) {
        this.scope.siteSettingsForServerAdmin.settings.siteCommentingSettings.commentingEnabled = value;
      }
    });

    this.scope.clearSavedRevisions = () => this.clearSavedRevisions(this.scope.siteId);

    if (!this.scope.hideRevisionHistoryLimitWarning) {
      // Async call to get the data for the Revision History Limit warning message, e.g. "Setting the limit to this will delete X revisions"
      const debouncedUpdate = _.debounce((newValue: number) => {
        this.scope.$evalAsync(() => {
          this.setExcessRevisionsMessage(newValue);
        });
      }, SiteSettingsForServerAdminFormCtrl.InputDebounceMs);

      // Kick off debounced update when contentVersionLimit changes
      this.scope.$watch('siteSettingsForServerAdmin.settings.contentVersionLimit', (value: number) => {
        this.scope.excessRevisionsMessage = '';
        if (_.isNumber(value)) {
          debouncedUpdate(value);
        }
      });
    }
  }

  private initializeUnsetValues(){
        // Initialize unset values so angular doesn't overwrite them with empty string
    if (this.scope.siteSettingsForServerAdmin && this.scope.siteSettingsForServerAdmin.settings) {
      if (!this.scope.siteSettingsForServerAdmin.settings.userQuotaEnabled) {
        this.scope.siteSettingsForServerAdmin.settings.userQuota = null;
      }
      if (!this.scope.siteSettingsForServerAdmin.settings.storageQuotaEnabled) {
        this.scope.siteSettingsForServerAdmin.settings.storageQuotaBytes = null;
      }
      if (!this.scope.siteSettingsForServerAdmin.settings.customSubscriptionEmailEnabled) {
        this.scope.siteSettingsForServerAdmin.settings.customSubscriptionEmail = angular.copy(this.scope.defaultSubscriptionsEmail);
      }
      if (!(this.scope.siteSettingsForServerAdmin.settings.contentVersionLimit >= 0)) {
        this.scope.siteSettingsForServerAdmin.settings.contentVersionLimit = null;
      }
      // Copy a scrubbed version of the inputted name into the ID field until the ID is edited
      if (this.scope.siteSettingsForServerAdmin.settings.urlName === '' && !this.scope.isDefaultSite) {
        this.scope.unbindNameWatcher = this.scope.$watch('siteSettingsForServerAdmin.settings.name', (value: string) => {
          this.scope.siteSettingsForServerAdmin.settings.urlName = _.isString(value) ? value.replace(SiteIdValidRegExp.InvalidFind, '') : '';
        });
      }
    }
    // The backend sends the subscriptionSettings object as part of the site settings iff
    // subscriptions are enabled at the server level. We check for its existence and put its value
    // into a boolean up front because ng-model will "helpfully" create the subscriptionSettings
    // object later during the digest to set up the 2-way binding for its inner fields. (TFS: 616257)
    this.scope.subscriptionsEnabledOnServer = !!this.scope.siteSettingsForServerAdmin.subscriptionSettings;
    this.scope.isSelfServiceScheduleEnabled = this.server.isSelfServiceScheduleEnabled();
    this.scope.isCustomScheduleSubscriptionForSiteEnabled = this.scope.subscriptionsEnabledOnServer ?
      this.scope.siteSettingsForServerAdmin.subscriptionSettings.customScheduleSubscriptionForSiteEnabled : false;

    this.scope.flowsOnServerEnabled = this.server.flowsOnServerEnabled();

    this.scope.catalogingOnServerEnabled = this.server.catalogingOnServerEnabled();
  }

  private revertChanges(): void {
    this.scope.siteSettingsForServerAdmin = angular.copy(this.master);
    // TODO: Use Angular 1.3 $validators/$validate and pull validation out of $parsers/$formatters
    _.each(this.scope.siteSettingsForServerAdminForm.$error, (error: ng.INgModelController[], key: string) => {
      if (error) {
        error[0].$setValidity(key, true);
      }
    });
  }

  // Used in edit site modal
  public save(): void {
    this.getSiteSettingsForServerAdminUpdatePromise();
  }

  private getSiteSettingsForServerAdminUpdatePromise(): ng.IPromise<any> {
    const updatePromises: ng.IPromise<any>[] = [];
    const site = angular.copy(this.scope.siteSettingsForServerAdmin);

    this.scope.localForm.busy,
    updatePromises.push(this.sitesResource.updateSiteSettingsForServerAdmin(
      site.id,
      site.settings,
      site.subscriptionSettings
    ));

    if (site.availability !== this.master.availability){
      if (site.availability === SiteAvailabilityState.ACTIVE) {
        updatePromises.push(this.sitesResource.activate([site.id]));
      } else {
        updatePromises.push(this.sitesResource.suspend([site.id]));
      }
    }

    // todo use ResultHelper.combineResults here instead of duplicating the logic
    return this.$q.all(updatePromises)
      .then((results: IResult[]) => {
        let errors: IError[] = [];

        angular.forEach(results, (result: IResult) => {
          if (result.errors && result.errors.length) {
            errors = errors.concat(result.errors);
          }
        });

        if (errors.length) {
          this.scope.sharedForm.state.updateFailure(errors, site);
          return this.$q.reject();
        } else {
          this.scope.isCustomScheduleSubscriptionForSiteEnabled = !!site.subscriptionSettings ?
            site.subscriptionSettings.customScheduleSubscriptionForSiteEnabled : false;
          this.updateSuccess(site);
        }
      })
      .finally(() => this.scope.localForm.busy = false);
  }

  private clearSavedRevisions(siteId: string) {
    this.clearSavedRevisionsAction.confirmDeleteSiteRevisions(siteId);
  }

  // Success/Error Handlers

  private updateSuccess(site: ISiteSettingsForServerAdmin): void {
    // Only need to update current view and state if we're in server or site settings
    if (!this.$state.includes('**.settings.**')) {
      return;
    }
    // Refresh session info when changing a setting that affects it
    if (site.settings.name !== this.master.settings.name ||
        site.settings.catalogingOnSiteEnabled !== this.master.settings.catalogingOnSiteEnabled ||
        site.settings.flowsOnSiteEnabled !== this.master.settings.flowsOnSiteEnabled) {
      this.server.refreshSessionInfo(); // Update site picker and ServerService
    }

    // Changed site url name in site settings page
    if (site.settings.urlName !== '' && site.settings.urlName !== this.master.settings.urlName) {
      this.$state.go('main.site.settings.siteSettings', {siteUrlName: site.settings.urlName}); // Reload into new url
    }
    this.master = angular.copy(site);
  }

  private setExcessRevisionsMessage(input: number): void {
    const params: IGetNumExcessRevisionsParams = {
      limit: input
    };

    // This code contains a bug, because the getNumExcessRevisions request is implicitly site-scoped,
    // but this UI can show up in the edit site settings dialog from the all sites list.
    // The effect is that we'll fetch the excess revisions info for whatever site the
    // server admin is signed into regardless of which site they're editing.
    // TFS: 815654
    this.server.sendRequest(new GetNumExcessRevisionsRequest(params))
      .then((result: IGetNumExcessRevisionsResult) => {
        if (input === this.scope.siteSettingsForServerAdmin.settings.contentVersionLimit) {
          const workbookRevisionCount = result.workbookRevisionCount;
          const datasourceRevisionCount = result.datasourceRevisionCount;
          const flowRevisionCount = result.flowRevisionCount;

          if (this.master.settings.flowsOnSiteEnabled &&
             (flowRevisionCount > 0 || workbookRevisionCount > 0 || datasourceRevisionCount > 0)) {
            this.scope.excessRevisionsMessage = this.$translate.instant('contentVersionLimitWarningWithWorkbookDataSourceAndFlowValues',
              { LIMIT_COUNT: L10n.localizedCount(input),
                EXCESS_WORKBOOK_COUNT: L10n.localizedCount(workbookRevisionCount),
                EXCESS_DATASOURCE_COUNT: L10n.localizedCount(datasourceRevisionCount),
                EXCESS_FLOW_COUNT: L10n.localizedCount(flowRevisionCount)});
          } else if (workbookRevisionCount > 0 || datasourceRevisionCount > 0) {
            this.scope.excessRevisionsMessage = this.$translate.instant('contentVersionLimitWarningWithWorkbookAndDataSourceValues',
              { LIMIT_COUNT: L10n.localizedCount(input),
                EXCESS_WORKBOOK_COUNT: L10n.localizedCount(workbookRevisionCount),
                EXCESS_DATASOURCE_COUNT: L10n.localizedCount(datasourceRevisionCount)});
          }
        }
      }
    );
  }

}

Module.instance().controller('SiteSettingsForServerAdminFormCtrl', SiteSettingsForServerAdminFormCtrl);
